import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { addMultipleUsers } from "../../features/admin/adminSlice";
import { updateMenu } from "../../features/menu/menuSlice";
import { getDataStats } from "../../API/Outputs";
import { selectLabels } from "../../constants/appConstants";
import { toErrorWithMessage } from "../../utils/error";
import { clearPageRequestCache } from "../../utils/requestCache";
import {
  DEFAULT_PRIORITY_ABSOLUTES,
  DEFAULT_PRIORITY_PERCENTS,
  setPriorityKeys,
} from "../../utils/priorityConfig";

// Remove trailing slash from API URL to prevent double-slash issues
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "https://clinica-server.replit.app").replace(/\/+$/, "");

const EMPTY_AUTH = {
  accessToken: null,
  refreshToken: null,
  user: null,
};

const normalizeAuth = (value) => {
  if (!value) {
    return { ...EMPTY_AUTH };
  }

  return {
    accessToken: value.accessToken ?? null,
    refreshToken: value.refreshToken ?? null,
    user: value.user ?? null,
  };
};

const readStoredAuth = () => {
  if (typeof window === "undefined") {
    return { ...EMPTY_AUTH };
  }

  const accessToken = window.localStorage.getItem("accessToken");
  const refreshToken = window.localStorage.getItem("refreshToken");
  const storedUser = window.localStorage.getItem("user");

  let user = null;
  if (storedUser) {
    try {
      user = JSON.parse(storedUser);
    } catch (error) {
      console.warn("Failed to parse stored user", error);
      window.localStorage.removeItem("user");
    }
  }

  return normalizeAuth({ accessToken, refreshToken, user });
};

const writeStoredAuth = (auth) => {
  if (typeof window === "undefined") {
    return;
  }

  const { accessToken, refreshToken, user } = normalizeAuth(auth);

  if (accessToken) {
    window.localStorage.setItem("accessToken", accessToken);
  } else {
    window.localStorage.removeItem("accessToken");
  }

  if (refreshToken) {
    window.localStorage.setItem("refreshToken", refreshToken);
  } else {
    window.localStorage.removeItem("refreshToken");
  }

  if (user) {
    try {
      window.localStorage.setItem("user", JSON.stringify(user));
    } catch (error) {
      console.warn("Failed to persist user", error);
    }
  } else {
    window.localStorage.removeItem("user");
  }
};

export const AuthContext = createContext(null);

const AuthProvider = () => {
  const [auth, setAuth] = useState(() => readStoredAuth());
  const [selectedUnmet, setSelectedUnmet] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isAuthenticated = Boolean(auth.accessToken && auth.user);

  const setAuthState = useCallback((updater) => {
    setAuth((prev) => {
      const previous = normalizeAuth(prev);
      const candidate =
        typeof updater === "function" ? updater(previous) : updater;
      const next = normalizeAuth(candidate);
      writeStoredAuth(next);
      return next;
    });
  }, []);

  const labelToKey = useMemo(() => {
    const map = {};
    Object.entries(selectLabels).forEach(([key, label]) => {
      if (label && !map[label]) {
        map[label] = key;
      }
    });
    return map;
  }, []);

  const getPriorityStorageKey = useCallback(
    () => `priorityKeys:${auth.user?.email?.toLowerCase() || "default"}`,
    [auth.user?.email],
  );

  const readStoredPriorities = useCallback(
    (storageKey) => {
      if (typeof window === "undefined") {
        return null;
      }

      try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) {
          return null;
        }

        const parsed = JSON.parse(raw);
        if (
          parsed &&
          Array.isArray(parsed.absolutes) &&
          Array.isArray(parsed.percents)
        ) {
          return parsed;
        }
      } catch (error) {
        console.warn("Failed to read stored priorities", error);
      }

      return null;
    },
    [],
  );

  const writeStoredPriorities = useCallback((storageKey, priorities) => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(priorities));
    } catch (error) {
      console.warn("Failed to persist priorities", error);
    }
  }, []);

  const toNumberKey = useCallback((value) => {
    if (!value) return value;
    return value.startsWith("Percent of ")
      ? value.replace("Percent of ", "Number of ")
      : value;
  }, []);

  const toPercentKey = useCallback((value) => {
    if (!value) return value;
    return value.startsWith("Number of ")
      ? value.replace("Number of ", "Percent of ")
      : value;
  }, []);

  const normalizePriorityKey = useCallback(
    (value) => {
      if (!value) return null;

      if (selectLabels[value]) {
        return value;
      }

      const fromLabel = labelToKey[value];
      if (fromLabel) {
        return fromLabel;
      }

      if (value.startsWith("Number of ") || value.startsWith("Percent of ")) {
        return value;
      }

      return null;
    },
    [labelToKey],
  );

  const extractPriorityValues = useCallback((payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.priorities)) return payload.priorities;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.results)) return payload.results;
    if (Array.isArray(payload.unmet_needs)) return payload.unmet_needs;
    return [];
  }, []);

  const buildPriorityKeys = useCallback(
    (rawPriorities) => {
      const absolutes = [];
      const percents = [];
      const seenAbs = new Set();
      const seenPct = new Set();

      rawPriorities.forEach((raw) => {
        const normalized = normalizePriorityKey(raw);
        if (!normalized) return;

        const numberKey = toNumberKey(normalized);
        const percentKey = toPercentKey(normalized);

        if (
          numberKey &&
          numberKey.startsWith("Number of ") &&
          !seenAbs.has(numberKey)
        ) {
          seenAbs.add(numberKey);
          absolutes.push(numberKey);
        }

        if (
          percentKey &&
          percentKey.startsWith("Percent of ") &&
          !seenPct.has(percentKey)
        ) {
          seenPct.add(percentKey);
          percents.push(percentKey);
        }
      });

      return { absolutes, percents };
    },
    [normalizePriorityKey, toNumberKey, toPercentKey],
  );

  useEffect(() => {
    if (!auth.accessToken || !auth.refreshToken) {
      setSelectedUnmet([]);
      return;
    }

    let cancelled = false;

    const storageKey = getPriorityStorageKey();
    const cachedPriorities = readStoredPriorities(storageKey);

    const applyPriorities = (priorities) => {
      setPriorityKeys(priorities);
    };

    if (cachedPriorities) {
      applyPriorities(cachedPriorities);
    }

    getDataStats("get_priorities", auth.accessToken, auth.refreshToken)
      .then((res) => {
        if (cancelled) return;

        const rawPriorities = extractPriorityValues(res);
        const { absolutes, percents } = buildPriorityKeys(rawPriorities);
        const hasValues = absolutes.length > 0 || percents.length > 0;

        if (hasValues) {
          const normalized = {
            absolutes:
              absolutes.length > 0 ? absolutes : DEFAULT_PRIORITY_ABSOLUTES,
            percents:
              percents.length > 0 ? percents : DEFAULT_PRIORITY_PERCENTS,
          };

          applyPriorities(normalized);
          writeStoredPriorities(storageKey, normalized);
          return;
        }

        if (cachedPriorities) {
          applyPriorities(cachedPriorities);
          return;
        }

        applyPriorities({
          absolutes: DEFAULT_PRIORITY_ABSOLUTES,
          percents: DEFAULT_PRIORITY_PERCENTS,
        });
      })
      .catch((error) => {
        console.error("Error fetching priorities", error);

        if (cachedPriorities) {
          applyPriorities(cachedPriorities);
          return;
        }

        applyPriorities({
          absolutes: DEFAULT_PRIORITY_ABSOLUTES,
          percents: DEFAULT_PRIORITY_PERCENTS,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    auth.accessToken,
    auth.refreshToken,
    buildPriorityKeys,
    extractPriorityValues,
    getPriorityStorageKey,
    readStoredPriorities,
    writeStoredPriorities,
  ]);

  const logOut = useCallback(() => {
    clearPageRequestCache();
    setAuthState(() => EMPTY_AUTH);
    setSelectedUnmet([]);

    dispatch(
      updateMenu({
        currentMenu: "users",
        currentMenuLabel: "Users",
      }),
    );

    navigate("/", { replace: true });
  }, [dispatch, navigate, setAuthState]);

  const jsonFetch = useCallback(
    async (
      path,
      { method = "GET", body, headers = {}, skipAuth = false, ...rest } = {},
    ) => {
      const makeRequest = async (token) => {
        const authHeaders = token
          ? { Authorization: `Bearer ${token}` }
          : undefined;

        const finalHeaders = {
          "Content-Type": "application/json",
          ...headers,
          ...authHeaders,
        };

        const requestInit = {
          method,
          ...rest,
          headers: finalHeaders,
        };

        if (body !== undefined) {
          requestInit.body =
            typeof body === "string" || body instanceof FormData
              ? body
              : JSON.stringify(body);
        }

        return fetch(`${API_BASE_URL}${path}`, requestInit);
      };

      try {
        let response = await makeRequest(skipAuth ? undefined : auth.accessToken);

        if (response.status === 401 && !skipAuth && auth.refreshToken) {
          const refreshedToken = await refreshTokenFunction(auth.refreshToken);

          if (!refreshedToken) {
            logOut();
            throw new Error("Your session has expired. Please sign in again.");
          }

          setAuthState((prev) => ({
            ...prev,
            accessToken: refreshedToken,
          }));

          response = await makeRequest(refreshedToken);
        }

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            errorText || `Request failed with status ${response.status}`,
          );
        }

        if (response.status === 204) {
          return null;
        }

        try {
          return await response.json();
        } catch (error) {
          if (error instanceof SyntaxError) {
            return null;
          }
          throw error;
        }
      } catch (error) {
        throw toErrorWithMessage(
          error,
          "Unable to complete the request. Please try again later.",
        );
      }
    },
    [auth.accessToken, auth.refreshToken, logOut, setAuthState],
  );

  const loginAction = useCallback(
    async (credentials) => {
      try {
        const res = await jsonFetch("/login", {
          method: "POST",
          body: credentials,
          skipAuth: true,
        });

        if (res?.message !== "Login successful") {
          throw new Error(res?.message || "Login failed");
        }

        const userData = {
          ...credentials,
          region: res.region,
          company: res.company,
          admin: res.admin,
          page_view: res.page_view,
          name: res.name,
        };

        setAuthState({
          accessToken: res.access_token,
          refreshToken: res.refresh_token,
          user: userData,
        });

        dispatch(
          updateMenu(
            res.admin
              ? { currentMenu: "users", currentMenuLabel: "Users" }
              : { currentMenu: "home", currentMenuLabel: "Home" },
          ),
        );

        navigate("/dashboard", { replace: true });
      } catch (error) {
        throw toErrorWithMessage(error, "Unable to sign in. Please try again.");
      }
    },
    [dispatch, jsonFetch, navigate, setAuthState],
  );

  const updatePassword = useCallback(
    async (data) => {
      if (!auth.user?.email) {
        throw new Error("User email is not available");
      }

      try {
        return await jsonFetch("/update_password", {
          method: "POST",
          body: {
            email: auth.user.email,
            new_password: data.newPassword,
          },
        });
      } catch (error) {
        throw toErrorWithMessage(
          error,
          "Unable to update the password. Please try again later.",
        );
      }
    },
    [auth.user?.email, jsonFetch],
  );

  const sendEmail = useCallback(
    async (data) => {
      try {
        return await jsonFetch("/contact", {
          method: "POST",
          body: {
            name: data.name,
            email: data.email,
            message: data.message,
          },
        });
      } catch (error) {
        throw toErrorWithMessage(
          error,
          "Unable to send your message right now. Please try again later.",
        );
      }
    },
    [jsonFetch],
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const bridge = {
      logOut,
      setAccessToken: (token) =>
        setAuthState((prev) => ({ ...prev, accessToken: token })),
      setRefreshToken: (token) =>
        setAuthState((prev) => ({ ...prev, refreshToken: token })),
      setUser: (nextUser) =>
        setAuthState((prev) => ({ ...prev, user: nextUser })),
    };

    window.clinicaAuth = bridge;

    return () => {
      if (window.clinicaAuth === bridge) {
        delete window.clinicaAuth;
      }
    };
  }, [logOut, setAuthState]);

  const fetchUserData = useCallback(async () => {
    if (!auth.accessToken) {
      return;
    }

    try {
      const userData = await jsonFetch("/get_all_users");
      if (userData) {
        dispatch(addMultipleUsers(userData));
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [auth.accessToken, dispatch, jsonFetch]);

  const contextValue = useMemo(
    () => ({
      accessToken: auth.accessToken,
      refreshToken: auth.refreshToken,
      user: auth.user,
      isAuthenticated,
      sendEmail,
      selectedUnmet,
      setSelectedUnmet,
      updatePassword,
      refreshTokenFunction,
      fetchUserData,
      loginAction,
      logOut,
    }),
    [
      auth.accessToken,
      auth.refreshToken,
      auth.user,
      fetchUserData,
      isAuthenticated,
      loginAction,
      logOut,
      selectedUnmet,
      sendEmail,
      updatePassword,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      <Outlet />
    </AuthContext.Provider>
  );
};

export default AuthProvider;

let refreshRequest = null;

export const refreshTokenFunction = async (refreshToken) => {
  if (!refreshToken) {
    return null;
  }

  if (!refreshRequest) {
    refreshRequest = fetch(`${API_BASE_URL}/token/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            errorText || `Failed to refresh token: ${response.status}`,
          );
        }

        const data = await response.json();
        const token = data?.access_token ?? null;

        if (!token) {
          throw new Error("Refresh response did not include an access token");
        }

        if (typeof window !== "undefined") {
          window.localStorage.setItem("accessToken", token);

          if (window.clinicaAuth?.setAccessToken) {
            window.clinicaAuth.setAccessToken(token);
          }
        }

        return token;
      })
      .catch((error) => {
        if (typeof window !== "undefined") {
          window.localStorage.removeItem("accessToken");
          window.localStorage.removeItem("refreshToken");

          if (window.clinicaAuth?.logOut) {
            window.clinicaAuth.logOut();
          }
        }

        console.error("Failed to refresh token", error);
        return null;
      })
      .finally(() => {
        refreshRequest = null;
      });
  }

  return refreshRequest;
};

export { default as RequireAuth } from "./RequireAuth";
